// src/components/pages/MembershipPage.js
import React, { useEffect, useMemo, useState } from "react";
import { db } from "../../firebase";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import StarsRoundedIcon from "@mui/icons-material/StarsRounded";
import LocalActivityRoundedIcon from "@mui/icons-material/LocalActivityRounded";
import { useTheme } from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { doc, updateDoc, serverTimestamp, getDoc } from "firebase/firestore";
import useUserProfileStore from "../../store/useUserProfileStore";

const DEFAULT_INSIDER = {
  paymentEnabled: false,
  monthlyPriceUsd: 9.99,
  freeBonusPoints: 100,
  trialDays: 90,
  messaging: {
    freeHeadline: "You're invited! Join Insider and start stacking points.",
    freeSubtitle:
      "Activate your free Insider status, get {{bonusPoints}} bonus points, and keep building your score for future Inssa rewards.",
    freeCtaLabel: "Activate Insider Points",
  },
};

const FEATURE_ITEMS = [
  {
    icon: <AutoAwesomeRoundedIcon fontSize="small" />,
    title: "Points Boost",
    body: "Start with a bonus, then keep earning through drops, invites, and milestones.",
  },
  {
    icon: <LocalActivityRoundedIcon fontSize="small" />,
    title: "Treasure Box Finds",
    body: "Use your Insider status to discover special Treasure Boxes and featured drops.",
  },
  {
    icon: <StarsRoundedIcon fontSize="small" />,
    title: "Future Rewards",
    body: "Your points stay with your account while we shape more ways to use them.",
  },
];

const renderTemplate = (template, vars) => {
  let out = String(template ?? "");
  Object.entries(vars || {}).forEach(([k, v]) => {
    out = out.replaceAll(`{{${k}}}`, String(v));
  });
  return out;
};

const MembershipPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  const [user, setUser] = useState(() => auth.currentUser || null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [error, setError] = useState(null);
  const [warning, setWarning] = useState(null);

  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  const [insiderCfg, setInsiderCfg] = useState(DEFAULT_INSIDER);

  const [profileLoading, setProfileLoading] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoadFailed, setProfileLoadFailed] = useState(false);
  const [profileReloadKey, setProfileReloadKey] = useState(0);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => setUser(u || null));
    return () => unsub();
  }, [auth]);

  useEffect(() => {
    let cancelled = false;

    async function loadConfig() {
      try {
        setConfigLoading(true);
        setConfigError(null);

        const ref = doc(db, "config", "app");
        const snap = await getDoc(ref);
        const data = snap.exists() ? snap.data() || {} : {};
        const fromDb = data.insiderSubscription || {};

        const merged = {
          ...DEFAULT_INSIDER,
          ...fromDb,
          messaging: {
            ...DEFAULT_INSIDER.messaging,
            ...(fromDb.messaging || {}),
          },
        };

        if (!cancelled) setInsiderCfg(merged);
      } catch (err) {
        console.error("Config fetch error:", err);
        if (!cancelled) setConfigError("Could not load Insider settings. Please try again.");
      } finally {
        if (!cancelled) setConfigLoading(false);
      }
    }

    loadConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!user?.uid) {
      setUserProfile(null);
      setProfileLoading(false);
      return;
    }

    let cancelled = false;

    async function loadProfile() {
      try {
        setProfileLoading(true);
        setProfileLoadFailed(false);
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);
        if (!cancelled) {
          setUserProfile(snap.exists() ? { ...(snap.data() || {}), uid: user.uid } : { uid: user.uid });
        }
      } catch (err) {
        console.warn("User profile fetch failed on MembershipPage:", err);
        if (!cancelled) {
          // Falling back to a bare { uid } renders the join upsell to whoever lands
          // here — including real Insiders, which is exactly the bug this page was
          // showing. The synced store profile is the best answer we have when the
          // read fails; only when there is none do we admit we cannot tell.
          const storedProfile = useUserProfileStore.getState().userProfile;
          const usableStoredProfile =
            storedProfile && String(storedProfile.uid || "").trim() === user.uid
              ? storedProfile
              : null;

          setError("Could not load your Insider status. Please try again.");
          setProfileLoadFailed(!usableStoredProfile);
          setUserProfile(usableStoredProfile || { uid: user.uid });
        }
      } finally {
        if (!cancelled) setProfileLoading(false);
      }
    }

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, profileReloadKey]);

  const isInsider = useMemo(() => {
    const tier = (userProfile?.subscriptionData?.tier || "").toLowerCase();
    const status = (userProfile?.subscriptionData?.subscriptionStatus || "").toLowerCase();
    if (tier !== "insider") return false;
    if (status) return status === "active";
    return true;
  }, [userProfile]);

  const bonusPoints = useMemo(() => {
    const n = Math.floor(Number(insiderCfg?.freeBonusPoints));
    return Number.isFinite(n) ? Math.max(0, n) : 100;
  }, [insiderCfg?.freeBonusPoints]);

  const freeHeadline = DEFAULT_INSIDER.messaging.freeHeadline;
  const freeSubtitle = useMemo(
    () =>
      renderTemplate(DEFAULT_INSIDER.messaging.freeSubtitle, {
        bonusPoints,
      }),
    [bonusPoints]
  );
  const freeCta = DEFAULT_INSIDER.messaging.freeCtaLabel;
  // Where to return after Insider activation. Prefer the ?returnTo= query param
  // (it survives the sign-in/sign-up detour that strips React Router location
  // state), falling back to location state for direct signed-in entries. Only
  // internal paths are honored (guards against open-redirect).
  const membershipReturnTo = useMemo(() => {
    const fromQuery = new URLSearchParams(location.search || "").get("returnTo") || "";
    const fromState =
      location.state?.returnTo ||
      (location.state?.capsuleId ? `/capsule/${location.state.capsuleId}` : "") ||
      "";
    const candidate = String(fromQuery || fromState || "").trim();
    return candidate.startsWith("/") && !candidate.startsWith("//") ? candidate : "";
  }, [location.search, location.state?.capsuleId, location.state?.returnTo]);

  const awardFreeInsiderSignupPoints = async () => {
    const functions = getFunctions();
    const award = httpsCallable(functions, "awardFreeInsiderSignupPoints");
    return award({});
  };

  const goToInsiderSuccessDestination = ({ warningText } = {}) => {
    const uid = auth?.currentUser?.uid || "anon";
    navigate(membershipReturnTo || "/", {
      replace: true,
      state: {
        showSuccess: true,
        successDialogKey: `hasShownSuccess-insider-${uid}`,
        successDialog: {
          type: "insider_joined",
          title: "Your Insider Points Are Live",
          subtitle: "Welcome to the Insider circle. Keep stacking points for future Inssa rewards.",
          rewardPoints: 0,
          receiptId: "insider_free_signup",
          warning: warningText || null,
        },
      },
    });
  };

  const handleFreeInsiderActivation = async () => {
    if (!user) {
      setError("Please sign in to activate Insider.");
      return;
    }
    if (isInsider) {
      navigate("/", { replace: true });
      return;
    }

    setLoading(true);
    setLoadingMessage("Activating your Insider points…");
    setError(null);
    setWarning(null);

    try {
      const userRef = doc(db, "users", user.uid);
      await updateDoc(userRef, {
        "subscriptionData.tier": "insider",
        "subscriptionData.subscriptionDate": serverTimestamp(),
        "subscriptionData.subscriptionID": "FREE-INSIDER",
        "subscriptionData.subscriptionStatus": "active",
      });

      let warn = null;
      try {
        await awardFreeInsiderSignupPoints();
      } catch (awardErr) {
        console.error("awardFreeInsiderSignupPoints failed:", awardErr);
        warn = "Insider status activated, but bonus points could not be awarded right now.";
        setWarning(warn);
      }

      setUserProfile((prev) => ({
        ...(prev || {}),
        uid: user.uid,
        subscriptionData: {
          ...((prev || {})?.subscriptionData || {}),
          tier: "insider",
          subscriptionStatus: "active",
          subscriptionID: "FREE-INSIDER",
        },
      }));

      goToInsiderSuccessDestination({ warningText: warn });
    } catch (err) {
      console.error("Free Insider activation error:", err);
      setError("Could not activate Insider points. Please try again later.");
      setLoading(false);
      setLoadingMessage("");
    }
  };

  if (configLoading || (user?.uid && profileLoading)) {
    return (
      <Container maxWidth="md" sx={{ my: 5 }}>
        <Box display="flex" alignItems="center" justifyContent="center" gap={2} sx={{ py: 6 }}>
          <CircularProgress size={22} />
          <Typography>Loading Insider points…</Typography>
        </Box>
      </Container>
    );
  }

  // We could not read the profile and have nothing cached to fall back on, so we do
  // not know whether this viewer is already an Insider. Offering "join" here is what
  // made active Insiders see an upsell; ask them to retry instead.
  if (profileLoadFailed && !isInsider) {
    return (
      <Container maxWidth="md" sx={{ py: 4.5 }}>
        <Stack spacing={2}>
          <Alert severity="error">
            We couldn&apos;t check your Insider status right now.
          </Alert>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.3}>
            <Button variant="contained" onClick={() => setProfileReloadKey((key) => key + 1)}>
              Try Again
            </Button>
            <Button variant="outlined" onClick={() => navigate(membershipReturnTo || "/", { replace: true })}>
              Go Back
            </Button>
          </Stack>
        </Stack>
      </Container>
    );
  }

  if (isInsider) {
    return (
      <Container maxWidth="md" sx={{ py: 4.5 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.2, sm: 3 },
            borderRadius: 2,
            border: `1px solid ${alpha(theme.colors.mediumBlue, 0.2)}`,
            background: alpha(theme.colors.mediumBlue, 0.08),
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" spacing={1.2} alignItems="flex-start">
              <WorkspacePremiumRoundedIcon sx={{ fontSize: { xs: 42, sm: 48 }, mt: 0.2 }} />
              <Typography
                variant="h4"
                sx={{ fontWeight: 900, fontSize: { xs: "1.72rem", sm: "2rem" }, lineHeight: 1.08 }}
              >
                You&apos;re an Insider
              </Typography>
            </Stack>
            <Typography sx={{ opacity: 0.9 }}>
              Your Insider status is active. Keep stacking points for future Inssa rewards.
            </Typography>
            <Grid container spacing={1.4}>
              {FEATURE_ITEMS.map((item) => (
                <Grid item xs={12} sm={4} key={item.title}>
                  <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 1.2, height: "100%" }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.7 }}>
                      {item.icon}
                      <Typography sx={{ fontWeight: 800 }}>{item.title}</Typography>
                    </Stack>
                    <Typography variant="body2" sx={{ opacity: 0.82 }}>
                      {item.body}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.3}>
              <Button variant="contained" onClick={() => navigate(membershipReturnTo || "/", { replace: true })}>
                Explore Insider Moments
              </Button>
              <Button variant="outlined" onClick={() => navigate("/points-ledger")}>
                View My Points
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4.5 }}>
      <Stack spacing={2}>
        {configError ? <Alert severity="warning">{configError}</Alert> : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {warning ? <Alert severity="warning">{warning}</Alert> : null}
        {loading && loadingMessage ? <Alert severity="info">{loadingMessage}</Alert> : null}

        <Paper
          elevation={0}
          sx={(muiTheme) => ({
            p: { xs: 2.2, sm: 3 },
            borderRadius: 2,
            color: muiTheme.palette.common.white,
            background: `linear-gradient(135deg, ${theme.colors.inssa} 0%, ${theme.colors.mediumBlue} 100%)`,
            border: `1px solid ${alpha(muiTheme.palette.common.white, 0.2)}`,
          })}
        >
          <Stack spacing={1.4}>
            <Stack direction="row" spacing={1.2} alignItems="flex-start">
              <WorkspacePremiumRoundedIcon sx={{ fontSize: { xs: 42, sm: 48 }, mt: 0.2 }} />
              <Typography
                variant="h4"
                sx={{ fontWeight: 900, fontSize: { xs: "1.72rem", sm: "2rem" }, lineHeight: 1.08 }}
              >
                {freeHeadline}
              </Typography>
            </Stack>
            <Typography sx={{ opacity: 0.94 }}>
              {freeSubtitle}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Chip label={`${bonusPoints} bonus points`} />
              <Chip label="Free to join" />
              <Chip label="Future rewards" />
            </Stack>
          </Stack>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.6 }, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1.3 }}>
            Ways to Earn
          </Typography>
          <Grid container spacing={1.4}>
            {FEATURE_ITEMS.map((item) => (
              <Grid item xs={12} sm={4} key={item.title}>
                <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 1.2, height: "100%" }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.7 }}>
                    {item.icon}
                    <Typography sx={{ fontWeight: 800 }}>{item.title}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ opacity: 0.82 }}>
                    {item.body}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Paper>

        <Paper elevation={0} sx={{ p: { xs: 2, sm: 2.6 }, borderRadius: 2, border: "1px solid", borderColor: "divider" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 1 }}>
            Join the Circle
          </Typography>

          <FormControlLabel
            sx={{ mb: 1 }}
            control={<Checkbox checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} />}
            label={
              <Typography variant="body2">
                I agree to the{" "}
                <a
                  href="https://www.kbean.com/kbean-creator-alliance-program-subscription-agreement/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Insider Terms
                </a>
              </Typography>
            }
          />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={1.2}>
              <Button
                variant="contained"
                fullWidth
                disabled={!acceptedTerms || loading}
                onClick={handleFreeInsiderActivation}
                sx={(muiTheme) => ({
                  py: 1.2,
                  fontWeight: 800,
                  ...(loading
                    ? {
                        color: muiTheme.palette.common.white,
                        "&.Mui-disabled": {
                          color: muiTheme.palette.common.white,
                          backgroundColor: alpha(muiTheme.palette.success.main, 0.72),
                          opacity: 1,
                        },
                      }
                    : null),
                })}
              >
                {loading ? "Activating Insider points…" : freeCta}
              </Button>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Bonus points are added to your account automatically after activation.
              </Typography>
            </Stack>
          )}
        </Paper>
      </Stack>
      <Dialog
        open={loading && Boolean(loadingMessage)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            textAlign: "center",
          },
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.72)",
          },
        }}
      >
        <DialogContent sx={{ py: 4, px: 3 }}>
          <Stack spacing={2} alignItems="center">
            <CircularProgress
              size={46}
              thickness={4.5}
              sx={(muiTheme) => ({ color: muiTheme.palette.success.main })}
            />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                Activating your Insider points
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                Adding your bonus points and getting your Insider status ready.
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default MembershipPage;
